import { t } from 'i18next';
import { AUTH_PATH, STATION_LOGIN_PATH, PASSWORD_RESET_PATH } from './apiPaths';
import {
  BadRequestError,
  FetchError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError
} from './errors';

export type FetchWithAuthConfig = {
  apiEnv: string;
  apiHost: string;
  getToken: () => string | null;
  setToken: (token: string) => void;
  getFreshToken?: () => Promise<string | null | undefined>;
  noAuthPaths?: string[];
};

let config: FetchWithAuthConfig | null = null;
const pendingGetRequests = new Map<string, Promise<unknown>>();

// デフォルトの認証不要パス（ログイン前でも呼び出せる API）。
// configureFetchWithAuth 側で常に強制マージされるため、
// 呼び出し側が noAuthPaths を渡しても上書きされない。
export const DEFAULT_NO_AUTH_PATHS = [
  PASSWORD_RESET_PATH,
  AUTH_PATH,
  STATION_LOGIN_PATH
];

export const isNoAuthPath = (path: string, noAuthPaths: string[]) => {
  // GET はクエリ文字列を含むため、パス部分だけで判定する
  const pathname = path.split('?')[0];
  return noAuthPaths.some(
    noAuthPath =>
      pathname === noAuthPath || pathname.startsWith(`${noAuthPath}/`)
  );
};

function createGetRequestKey(
  url: string,
  idToken: string | null,
  options: RequestInit
) {
  const headers = Array.from(new Headers(options.headers).entries()).sort(
    ([a], [b]) => a.localeCompare(b)
  );
  return JSON.stringify([url, idToken, headers]);
}

export function configureFetchWithAuth(nextConfig: FetchWithAuthConfig) {
  // デフォルトの認証不要パスは常に強制マージする。
  // 呼び出し側が渡した noAuthPaths によって全体が上書きされ、
  // ログインページ等の公開 API（/auth、/stations/login 等）が
  // 認証必須と誤判定されるのを防ぐため。
  // 呼び出し側は自パッケージ／自モジュールで追加する公開パスのみを渡せばよい。
  config = {
    ...nextConfig,
    noAuthPaths: [...DEFAULT_NO_AUTH_PATHS, ...(nextConfig.noAuthPaths ?? [])]
  };
}

export function getFetchWithAuthConfig() {
  return config;
}

export async function fetchWithAuth<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  if (!config) {
    throw new Error('fetchWithAuth is not configured');
  }
  const currentConfig = config;

  // local / remotetest モードでは Vite のプロキシ経由（相対パス）とする。
  // それ以外のモードでは apiHost 直指定（絶対 URL）とする。
  const url =
    currentConfig.apiEnv === 'local' || currentConfig.apiEnv === 'remotetest'
      ? path
      : `${currentConfig.apiHost}${path}`;
  const noAuthPaths = currentConfig.noAuthPaths ?? [
    PASSWORD_RESET_PATH,
    AUTH_PATH,
    STATION_LOGIN_PATH
  ];
  const isTokenRequired = !isNoAuthPath(path, noAuthPaths);
  const idToken = currentConfig.getToken();
  const isBinaryBody =
    options.body instanceof FormData || options.body instanceof Blob;

  if (isTokenRequired && !idToken) {
    throw new UnauthorizedError(t('error.authRequired'));
  }

  const execute = async (): Promise<T> => {
    try {
      const doFetch = async (token: string | null) => {
        return await fetch(url, {
          ...options,
          headers: {
            ...(options.headers || {}),
            ...(!isBinaryBody && { 'Content-Type': 'application/json' }),
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
      };

      let res = await doFetch(idToken);

      if (
        res.status === 401 &&
        isTokenRequired &&
        currentConfig.getFreshToken
      ) {
        const freshToken = await currentConfig.getFreshToken();
        if (freshToken) {
          currentConfig.setToken(freshToken);
          res = await doFetch(freshToken);
        }
      }

      if (res.status === 401) {
        throw new UnauthorizedError(t('error.authExpired'));
      }

      const result = await res.json().catch(() => null);
      console.log('res.url', res.url);
      console.log('result', result);

      if (!res.ok) {
        console.error(`fetchWithAuth failed: ${res.url} ${res.status}`);
        if (res.status === 400 || res.status === 409) {
          throw new BadRequestError(
            result?.message || res.statusText,
            result,
            res.status
          );
        } else if (res.status === 403) {
          throw new ForbiddenError(t('error.forbidden'));
        } else if (res.status === 404) {
          throw new NotFoundError(t('error.dataNotFoundError'));
        } else {
          throw new FetchError(res, t('error.systemError'));
        }
      }

      if (result?.success === false) {
        throw new BadRequestError(
          result?.message || t('error.systemError'),
          result
        );
      }

      return result as T;
    } catch (e) {
      console.error('[fetchWithAuth] error:', e);
      throw e;
    }
  };

  const method = (options.method ?? 'GET').toUpperCase();
  if (method !== 'GET') {
    return execute();
  }

  const requestKey = createGetRequestKey(url, idToken, options);
  const pendingRequest = pendingGetRequests.get(requestKey);
  if (pendingRequest) return pendingRequest as Promise<T>;

  const request = execute();
  pendingGetRequests.set(requestKey, request);
  const clearPendingRequest = () => {
    if (pendingGetRequests.get(requestKey) === request) {
      pendingGetRequests.delete(requestKey);
    }
  };
  void request.then(clearPendingRequest, clearPendingRequest);
  return request;
}
